import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ChatBot.scss';
import { detectRegion } from '../../config/locations';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  navTarget?: string | null;
  formSent?: boolean;
  contactSent?: boolean;
  showAppointment?: boolean;
}

interface ChatBotConfig {
  apiEndpoint?: string;
  assistantName?: string;
  assistantSubtitle?: string;
  welcomeMessage?: string;
  buttonLabel?: string;
  leadEmail?: string;
  errorMessage?: string;
  confirmationMessage?: string;
  placeholder?: string;
}

const WELCOME: Message = {
  role: 'assistant',
  content: "Hi! I'm the ER-HR Assistant. Ask me anything about our HR services, consulting, or how we can help your business.",
};

const FORM_TAG_RE = /\[FORM:(\{[\s\S]*?\})\]/;
const CONTACT_TAG_RE = /\[CONTACT:(\{[\s\S]*?\})\]/;
const NAV_TAG_RE = /\[NAV:(\/[^\]]*?)\]/;
const APPOINTMENT_TAG_RE = /\[APPOINTMENT\]/;

const SCHEDULE_APPOINTMENT_PATH = '/schedule-appointment';

const MAX_MESSAGE_LENGTH = 500;
const MAX_MESSAGES = 20;

const VALID_PATHS = [
  '/',
  '/who-we-are',
  '/contact',
  '/transitional-hr',
  '/strategic-hr/employee-engagement',
  '/strategic-hr/performance-management',
  '/strategic-hr/hr-systems',
  '/strategic-hr/benefits-strategy',
  '/strategic-hr/policies-procedures',
  '/strategic-hr/onboarding-offboarding',
  '/pricing',
];

const PATH_LABELS: Record<string, string> = {
  '/': 'Homepage',
  '/who-we-are': 'Who We Are',
  '/contact': 'Contact',
  '/transitional-hr': 'Transitional HR',
  '/strategic-hr/employee-engagement': 'Employee Engagement',
  '/strategic-hr/performance-management': 'Performance Management',
  '/strategic-hr/hr-systems': 'HR Systems',
  '/strategic-hr/benefits-strategy': 'Benefits Strategy',
  '/strategic-hr/policies-procedures': 'Policies & Procedures',
  '/strategic-hr/onboarding-offboarding': 'Onboarding & Offboarding',
  '/pricing': 'Services & Pricing',
};

function stripAllTags(text: string) {
  return text
    .replace(FORM_TAG_RE, '')
    .replace(CONTACT_TAG_RE, '')
    .replace(NAV_TAG_RE, '')
    .replace(APPOINTMENT_TAG_RE, '')
    .trim();
}

async function submitLeadForm(
  data: { name: string; email: string; company: string; service: string },
  leadEmail: string
) {
  await fetch(`https://formsubmit.co/ajax/${leadEmail}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      name: data.name,
      email: data.email,
      company: data.company,
      service: data.service,
      message: 'Inquiry submitted via website chat widget.',
      _subject: `Chat inquiry from ${data.name} — ${data.service}`,
      _template: 'table',
      _honeypot: '',
      _captcha: 'false',
    }),
  });
}

async function submitContactMessage(
  data: { name: string; email: string; message: string },
  leadEmail: string
) {
  await fetch(`https://formsubmit.co/ajax/${leadEmail}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      name: data.name,
      email: data.email,
      message: data.message,
      _subject: `Chat message from ${data.name}`,
      _template: 'table',
      _honeypot: '',
      _captcha: 'false',
    }),
  });
}

export function ChatBot({
  apiEndpoint = '/api/chat',  
  assistantName = 'ER-HR Assistant',
  assistantSubtitle = 'Ask about our services',
  buttonLabel = 'Ask AI',
  leadEmail = 'contact@erhrglobal.com',
  errorMessage = 'Something went wrong. Please try again or contact us at contact@erhrglobal.com.',
  confirmationMessage = 'Your details have been sent. We\'ll be in touch shortly.',
  placeholder = 'Ask a question...',
}: ChatBotConfig) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  useEffect(() => {
    if (!isOpen) return;

    document.body.classList.add('chatbot-open');

    // Don't auto-focus on mobile — keyboard would immediately cover the welcome message
    if (window.innerWidth >= 480) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }

    return () => {
      document.body.classList.remove('chatbot-open');
    };
  }, [isOpen]);

  // Handle mobile keyboard: panel covers full screen, padding pushes content above keyboard
  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      if (window.innerWidth >= 480) return;
      const panel = document.querySelector('.chatbot-panel') as HTMLElement;
      if (!panel) return;

      const top = vv.offsetTop;
      const keyboardHeight = window.innerHeight - vv.height - top;

      // Panel stretches from visual viewport top to bottom of layout viewport
      panel.style.top = `${top}px`;
      panel.style.height = `${window.innerHeight - top}px`;
      // Padding pushes flex content above the keyboard
      panel.style.paddingBottom = keyboardHeight > 0 ? `${keyboardHeight}px` : '';

      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      const panel = document.querySelector('.chatbot-panel') as HTMLElement;
      if (panel) {
        panel.style.top = '';
        panel.style.height = '';
        panel.style.paddingBottom = '';
      }
    };
  }, [isOpen]);

  const userMessageCount = messages.filter(m => m.role === 'user').length;
  const conversationLimitReached = userMessageCount >= MAX_MESSAGES;

  async function sendMessage() {
    const text = input.trim();
    if (!text || isStreaming || conversationLimitReached) return;

    const userMsg: Message = { role: 'user', content: text };
    const history = messages.filter(m => m !== WELCOME);

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsStreaming(true);
    setStreamingText('');

    try {
      const res = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history, region: detectRegion() }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Request failed: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        full += chunk;
        setStreamingText(stripAllTags(full));
      }

      // Build the assistant message with inline action metadata
      const assistantMsg: Message = { role: 'assistant', content: stripAllTags(full) };

      // Lead capture form
      const formMatch = full.match(FORM_TAG_RE);
      if (formMatch) {
        try {
          const formData = JSON.parse(formMatch[1]);
          await submitLeadForm(formData, leadEmail);
          assistantMsg.formSent = true;
        } catch {
          // form submission failed silently — user still has the email fallback
        }
      }

      // Contact message form
      const contactMatch = full.match(CONTACT_TAG_RE);
      if (contactMatch) {
        try {
          const contactData = JSON.parse(contactMatch[1]);
          await submitContactMessage(contactData, leadEmail);
          assistantMsg.contactSent = true;
        } catch {
          // contact submission failed silently
        }
      }

      // Navigation
      const navMatch = full.match(NAV_TAG_RE);
      if (navMatch && VALID_PATHS.includes(navMatch[1])) {
        assistantMsg.navTarget = navMatch[1];
      }

      // Appointment
      if (APPOINTMENT_TAG_RE.test(full)) {
        assistantMsg.showAppointment = true;
      }

      setMessages(prev => [...prev, assistantMsg]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: errorMessage },
      ]);
    } finally {
      setIsStreaming(false);
      setStreamingText('');
    }
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <>
      {/* Chat panel */}
      {isOpen && (
        <div className="chatbot-panel">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                AI
              </div>
              <div>
                <p className="chatbot-title">{assistantName}</p>
                <p className="chatbot-subtitle">{assistantSubtitle}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="chatbot-close"
              aria-label="Close chat"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg, i) => (
              <div key={i}>
                <div className={`chatbot-message chatbot-message--${msg.role}`}>
                  <div className="chatbot-message-content">
                    {msg.content}
                  </div>
                </div>

                {msg.formSent && (
                  <div className="chatbot-confirmation mt-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                    {confirmationMessage}
                  </div>
                )}

                {msg.contactSent && (
                  <div className="chatbot-confirmation mt-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                    Your message has been sent to our team. We'll reply shortly.
                  </div>
                )}

                {msg.navTarget && (
                  <div className="chatbot-action-row">
                    <button
                      className="chatbot-nav-btn mt-2"
                      onClick={() => {
                        navigate(msg.navTarget!);
                        setMessages(prev => prev.map((m, idx) => idx === i ? { ...m, navTarget: null } : m));
                        setIsOpen(false);
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                      Go to {PATH_LABELS[msg.navTarget] || msg.navTarget}
                    </button>
                  </div>
                )}

                {msg.showAppointment && (
                  <div className="chatbot-action-row">
                    <a
                      className="chatbot-appointment-btn mt-2"
                      href={SCHEDULE_APPOINTMENT_PATH}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      Book an Appointment
                    </a>
                  </div>
                )}
              </div>
            ))}

            {/* Streaming message */}
            {isStreaming && (
              <div className="chatbot-message chatbot-message--assistant">
                <div className="chatbot-message-content">
                  {streamingText || (
                    <span className="chatbot-typing">
                      <span style={{ animationDelay: '0ms' }} />
                      <span style={{ animationDelay: '150ms' }} />
                      <span style={{ animationDelay: '300ms' }} />
                    </span>
                  )}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chatbot-input-area">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
              onKeyDown={handleKey}
              placeholder={conversationLimitReached ? 'Message limit reached — please start a new chat.' : placeholder}
              disabled={isStreaming || conversationLimitReached}
              maxLength={MAX_MESSAGE_LENGTH}
              className="chatbot-input"
            />
            <button
              onClick={sendMessage}
              disabled={isStreaming || !input.trim() || conversationLimitReached}
              className="chatbot-send"
              aria-label="Send message"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Floating button — hidden on mobile when chat is fullscreen open */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="chatbot-trigger"
          aria-label="Open AI assistant"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span>{buttonLabel}</span>
          <span className="chatbot-pulse" />
        </button>
      )}
    </>
  );
}

export default ChatBot;
